import React, {useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {Button, Tag, Input, Modal, message, Tooltip} from 'antd';
import type {TableColumnsType} from 'antd';
import {ReadOutlined, CloseCircleOutlined} from '@ant-design/icons';
import {useGetOrders, useCancelOrderById} from 'hooks/order/useGetOrder';
import type {SearchProps} from 'antd/es/input/Search';

import {TableCustom} from 'components/element/TableCustom';
import {PaginationCustom} from 'components/element/PaginationCustom';
import {addKeyData, formatNumberValue, formatDate} from 'utils';
import {
  ORDER_STATUS,
  ORDER_STATUS_COLOR,
  ORDER_STATUS_LABEL,
} from 'types/order';

const QUICK_CANCEL_REASONS = [
  'Khách hàng yêu cầu hủy',
  'Quán hết món / đóng cửa',
  'Không tìm thấy tài xế giao hàng',
  'Sai thông tin địa chỉ / số điện thoại',
  'Đơn hàng trùng lặp',
];

export const Orders: React.FC = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({});
  const {data: getData, isLoading, refetch} = useGetOrders(filters);
  const {mutateAsync: cancelOrder} = useCancelOrderById();

  const [cancelModalVisible, setCancelModalVisible] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [isSubmittingCancel, setIsSubmittingCancel] = useState(false);

  const columns: TableColumnsType = [
    {title: 'Mã Order', dataIndex: 'code', key: 'code', align: 'center'},
    // Q: nút Hủy đơn chuyển ra cột đầu (ngay sau mã đơn), có modal điền lý do hủy
    {
      title: 'Hủy',
      dataIndex: '_id',
      key: 'cancel',
      align: 'center',
      width: 90,
      render: (_id: string, record: any) => {
        if (
          [
            ORDER_STATUS.Completed,
            ORDER_STATUS.Canceled,
            ORDER_STATUS.AutoCompleted,
          ].includes(record?.status)
        )
          return null;
        return (
          <Button
            danger
            size="small"
            onClick={() => {
              setSelectedOrder(record);
              setCancelReason('');
              setCancelModalVisible(true);
            }}>
            Hủy
          </Button>
        );
      },
    },
    {
      title: 'Địa chỉ người nhận',
      dataIndex: 'addressReceiver',
      key: 'addressReceiver',
      align: 'center',
    },
    // Q: thêm cột SĐT người nhận
    {
      title: 'SĐT người nhận',
      dataIndex: 'numberPhoneReceiver',
      key: 'numberPhoneReceiver',
      align: 'center',
      render: (data: string) => data || <span style={{color: 'red'}}>Chưa có SĐT</span>,
    },
    {
      title: 'Tên Shop',
      dataIndex: 'shop',
      key: 'shop',
      align: 'center',
      render: (data: any) => {
        return <div>{data?.name}</div>;
      },
    },
    {
      title: 'Tên người nhận',
      dataIndex: 'client',
      key: 'client',
      align: 'center',
      render: (data: any) => {
        return <div>{data?.name}</div>;
      },
    },
    {
      title: 'Phụ phí',
      dataIndex: 'subFee',
      key: 'subFee',
      align: 'center',
      render: data => {
        return (
          <div>
            <p>{formatNumberValue(data)}</p>
          </div>
        );
      },
    },
    {
      title: 'Phí Ship',
      dataIndex: 'shipFee',
      key: 'shipFee',
      align: 'center',
      render: data => {
        return (
          <div>
            <p>{formatNumberValue(data)}</p>
          </div>
        );
      },
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      align: 'center',
      render: (data: string, record: any) => {
        return (
          <div className="flex flex-col items-center gap-1">
            <Tag color={ORDER_STATUS_COLOR[data] || 'default'}>
              {ORDER_STATUS_LABEL[data as ORDER_STATUS] || data.toLocaleUpperCase()}
            </Tag>
            {record?.cancelReason && (
              <Tooltip
                title={record.cancelReason}
                placement="topLeft"
                overlayInnerStyle={{maxWidth: 350, wordBreak: 'break-word'}}>
                <span className="text-xs text-red-500 italic max-w-[150px] truncate cursor-pointer block">
                  Lý do: {record.cancelReason}
                </span>
              </Tooltip>
            )}
          </div>
        );
      },
      filters: [
        {value: ORDER_STATUS.Initialize, text: ORDER_STATUS_LABEL[ORDER_STATUS.Initialize]},
        {value: ORDER_STATUS.Waiting, text: ORDER_STATUS_LABEL[ORDER_STATUS.Waiting]},
        {value: ORDER_STATUS.DriverVerify, text: ORDER_STATUS_LABEL[ORDER_STATUS.DriverVerify]},
        {value: ORDER_STATUS.DriverConfirmed, text: ORDER_STATUS_LABEL[ORDER_STATUS.DriverConfirmed]},
        {value: ORDER_STATUS.DriverToShop, text: ORDER_STATUS_LABEL[ORDER_STATUS.DriverToShop]},
        {value: ORDER_STATUS.ClientPaying, text: ORDER_STATUS_LABEL[ORDER_STATUS.ClientPaying]},
        {value: ORDER_STATUS.DriverConfirmingPayment, text: ORDER_STATUS_LABEL[ORDER_STATUS.DriverConfirmingPayment]},
        {value: ORDER_STATUS.PaymentFailure, text: ORDER_STATUS_LABEL[ORDER_STATUS.PaymentFailure]},
        {value: ORDER_STATUS.DriverDelivery, text: ORDER_STATUS_LABEL[ORDER_STATUS.DriverDelivery]},
        {value: ORDER_STATUS.Completed, text: ORDER_STATUS_LABEL[ORDER_STATUS.Completed]},
        {value: ORDER_STATUS.AutoCompleted, text: ORDER_STATUS_LABEL[ORDER_STATUS.AutoCompleted]},
        {value: ORDER_STATUS.Canceled, text: ORDER_STATUS_LABEL[ORDER_STATUS.Canceled]},
      ],
    },
    {
      title: 'Ngày đặt',
      dataIndex: 'createdAt',
      key: 'createdAt',
      align: 'center',
      render: data => {
        return (
          <div>
            <p>{formatDate(data)}</p>
          </div>
        );
      },
    },
    {
      title: '',
      dataIndex: '',
      key: 'action',
      align: 'center',
      width: 200,
      render: (record: any) => {
        return (
          <div className="flex justify-center gap-4 px-0">
            <Button
              onClick={() => {
                navigate(`/orders/${record?._id}`, {
                  state: {from: `/orders`},
                });
              }}
              icon={<ReadOutlined />}
              className="!border-white	 bg-[#eca52b] hover:!bg-[#edb555] !text-white !font-medium">
              Chi tiết đơn hàng
            </Button>
            {/* <Button className="!border-white bg-[#ed250e] hover:!bg-[#f25c4b] !text-white !font-medium">
              Xoá
            </Button> */}
          </div>
        );
      },
    },
  ];
  const handleTableChange = (filter: any, sorter: any) => {
    const {status} = filter;
    if (status) {
      setFilters({...filters, status: status});
    } else {
      setFilters({...filters, status: []});
    }
  };
  const onSeachValue: SearchProps['onSearch'] = (value, _e, info) => {
    setFilters({...filters, code: value});
  };
  return (
    <div className="container mx-auto">
      <div className="flex-col gap-4">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold">Orders</h2>
          <div>
            <Input.Search
              className="w-96"
              placeholder="Tìm kiếm mã đơn hàng"
              onSearch={onSeachValue}
              enterButton
            />
          </div>
        </div>
        <div className=" flex flex-col items-end gap-3">
          <TableCustom
            isLoading={isLoading}
            columns={columns}
            data={addKeyData(getData?.result)}
            onChange={handleTableChange}
          />
          <PaginationCustom
            totalPage={getData?.totalPage ?? 1}
            current={getData?.page ?? 1}
            pageSize={getData?.pageSize ?? 10}
            onChange={(page, pageSize) => {
              console.log(page, pageSize);
              setFilters({...filters, page, pageSize});
            }}
            onShowSizeChange={(current, size) => {
              setFilters({...filters, page: current, pageSize: size});
            }}
          />
        </div>
      </div>

      {/* Modal xác nhận hủy đơn hàng và nhập lý do */}
      <Modal
        title={
          <div className="flex items-center gap-2 text-red-600 font-bold text-lg">
            <CloseCircleOutlined />
            <span>Xác nhận hủy đơn hàng #{selectedOrder?.code || ''}</span>
          </div>
        }
        open={cancelModalVisible}
        onCancel={() => {
          setCancelModalVisible(false);
          setSelectedOrder(null);
          setCancelReason('');
        }}
        okText="Xác nhận hủy đơn"
        okButtonProps={{danger: true, loading: isSubmittingCancel}}
        cancelText="Đóng"
        onOk={async () => {
          const reasonToSubmit = cancelReason.trim();
          if (!reasonToSubmit) {
            message.warning('Vui lòng nhập hoặc chọn lý do hủy đơn hàng');
            return;
          }
          try {
            setIsSubmittingCancel(true);
            await cancelOrder({
              id: selectedOrder?._id,
              reason: reasonToSubmit,
            });
            message.success('Hủy đơn hàng thành công');
            setCancelModalVisible(false);
            setSelectedOrder(null);
            setCancelReason('');
            refetch();
          } catch (e: any) {
            message.error(e?.response?.data?.message || 'Hủy đơn hàng thất bại');
          } finally {
            setIsSubmittingCancel(false);
          }
        }}>
        <div className="flex flex-col gap-3 py-2">
          <p className="text-gray-600 text-sm">
            Bạn đang thao tác hủy đơn hàng <strong>#{selectedOrder?.code}</strong>.
            Vui lòng nhập hoặc chọn lý do hủy:
          </p>

          <Input.TextArea
            rows={3}
            placeholder="Nhập lý do hủy đơn hàng..."
            value={cancelReason}
            onChange={e => setCancelReason(e.target.value)}
          />

          <div>
            <div className="text-xs text-gray-500 mb-1.5 font-medium">Gợi ý lý do nhanh:</div>
            <div className="flex flex-wrap gap-1.5">
              {QUICK_CANCEL_REASONS.map(r => (
                <Tag
                  key={r}
                  className="cursor-pointer hover:border-red-400 select-none py-1 text-xs"
                  color={cancelReason === r ? 'red' : 'default'}
                  onClick={() => setCancelReason(r)}>
                  {r}
                </Tag>
              ))}
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};
